import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { useTranslation } from '../../contexts/LanguageContext'
import type { PersonalInfo } from '../../types/cv'
import {
  validateImageFile,
  compressImage,
  getAcceptedImageTypes,
} from '../../utils/imageUtils'

interface PhotoFormProps {
  data: PersonalInfo | undefined
  onChange: (data: Partial<PersonalInfo>) => void
}

export function PhotoForm({ data, onChange }: PhotoFormProps) {
  const { t } = useTranslation()
  const [photoError, setPhotoError] = useState<string>('')
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false)

  const handlePhotoDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]
      if (!file) return

      setPhotoError('')
      const validation = validateImageFile(file)

      if (!validation.valid) {
        setPhotoError(validation.error || t.photoForm.validationError)
        return
      }

      setIsUploadingPhoto(true)
      try {
        const compressedDataUrl = await compressImage(file)
        onChange({ photo: compressedDataUrl })
      } catch {
        setPhotoError(t.photoForm.processError)
      } finally {
        setIsUploadingPhoto(false)
      }
    },
    [onChange, t]
  )

  const removePhoto = useCallback(() => {
    onChange({ photo: undefined })
    setPhotoError('')
  }, [onChange])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handlePhotoDrop,
    accept: getAcceptedImageTypes(),
    maxFiles: 1,
    multiple: false,
  })

  return (
    <div>
      {data?.photo ? (
        <div className="flex items-center gap-4">
          <div className="relative">
            <img
              src={data.photo}
              alt={t.sections.photo}
              className="w-24 h-24 rounded-lg object-cover border-2 border-slate-200"
            />
            <button
              type="button"
              onClick={removePhoto}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-sm font-bold transition-colors shadow-md"
              title={t.photoForm.remove}
            >
              &times;
            </button>
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-700">{t.photoForm.photoLoaded}</p>
            <p className="text-xs text-slate-500 mt-1">
              {t.photoForm.changeHint}
            </p>
          </div>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-blue-500 bg-blue-50'
              : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50'
          } ${isUploadingPhoto ? 'opacity-50 pointer-events-none' : ''}`}
        >
          <input {...getInputProps()} />
          <div className="flex flex-col items-center gap-2">
            <svg
              className="w-10 h-10 text-slate-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            {isUploadingPhoto ? (
              <p className="text-sm text-slate-500">{t.photoForm.processing}</p>
            ) : isDragActive ? (
              <p className="text-sm text-blue-600 font-medium">{t.photoForm.dropHere}</p>
            ) : (
              <>
                <p className="text-sm text-slate-600">
                  {t.photoForm.dragDrop}
                </p>
                <p className="text-xs text-slate-400">{t.photoForm.maxSize}</p>
              </>
            )}
          </div>
        </div>
      )}

      {photoError && (
        <p className="mt-2 text-sm text-red-500">{photoError}</p>
      )}

      <p className="mt-3 text-xs text-slate-500">
        {t.photoForm.templateHint}
      </p>
    </div>
  )
}
